import { React, useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import "./point-record-style.css";
import Loading from "../../dialog/loading/loading";
import GoogleLoginDialog from "../../dialog/google-login/GoogleLoginDialog";
import ConfirmDialog from "../../dialog/confirm/confirm-dialog";
import UserService from "../../../service/user-service";
import AuthService from "../../../service/auth-service";
import SiteFooter from "../../custom/site-footer/site-footer";
import useIsMobile from "../../../hooks/useIsMobile";

const ITEMS_PER_PAGE = 5;

//timestamp(ms) → "YYYY/MM/DD HH:mm"
const formatTime = (ms) => {
  if (!ms) return "";
  const d = new Date(ms);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}/${pad(d.getMonth() + 1)}/${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

//正/負數加上符號（負數本身已有 - 號）
const formatChange = (n) => (n > 0 ? `+${n}` : `${n}`);

const PointRecordCompontent = ({ setShowServiceMenu, setShowPersonalMenu }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const registerFrom = location.pathname;

  const isMobile = useIsMobile();
  const [currentPage, setCurrentPage] = useState(1);
  const [records, setRecords] = useState([]); //累積已拉的紀錄 buffer
  const [totalRecords, setTotalRecords] = useState(0); //總筆數
  const [isLoading, setIsLoading] = useState(true);

  //—— Auth gating ——
  const [showLoginDialog, setShowLoginDialog] = useState(false);
  const [showRegisterPrompt, setShowRegisterPrompt] = useState(false);

  const fetchedRef = useRef(false);

  //初次載入：先 auth gating，通過才拉資料
  useEffect(() => {
    if (fetchedRef.current) return;
    const isLogin = AuthService.getLoginStatus();
    const profile = AuthService.getUserProfile();
    if (!isLogin || !profile) {
      setIsLoading(false);
      setShowLoginDialog(true);
      return;
    }
    if (profile.userValidCode !== "2") {
      setIsLoading(false);
      setShowRegisterPrompt(true);
      return;
    }
    fetchedRef.current = true;
    fetchMore(0, ITEMS_PER_PAGE);
  }, []);

  //補拉紀錄（loadmore）— 顯示 loading dialog 至少 1 秒
  //  解構加 fallback：防 validCode=1 時 server 回不含 records 的 shape 導致 crash
  const fetchMore = async (skip, limit) => {
    setIsLoading(true);
    const MIN_LOADING_MS = 1000;
    const start = Date.now();

    try {
      const res = await UserService.getPointRecords(skip, limit);
      if (res.data?.status) {
        const { records: newRecords = [], total = 0 } = res.data.data || {};
        setRecords((prev) => [...prev, ...newRecords]);
        setTotalRecords(total);
      }
    } catch (e) {
      console.log(e);
    } finally {
      const remaining = MIN_LOADING_MS - (Date.now() - start);
      if (remaining > 0) {
        await new Promise((r) => setTimeout(r, remaining));
      }
      setIsLoading(false);
    }
  };

  const handleLoginSuccess = async ({ idToken } = {}) => {
    if (!idToken) return;
    try {
      const res = await AuthService.firebaseLogin(idToken);
      if (res.data.status) {
        AuthService.login(res.data.data);
        setShowLoginDialog(false);
        if (res.data.data.userValidCode !== "2") {
          navigate("/register", { state: { from: registerFrom } });
        } else {
          fetchedRef.current = true;
          fetchMore(0, ITEMS_PER_PAGE);
        }
      }
    } catch (e) {
      console.error("[PointRecord] firebase-login 失敗:", e);
    }
  };

  //點到指定頁，缺資料就補拉，最後滾回頁面最上方
  const goToPage = async (targetPage) => {
    const needed = targetPage * ITEMS_PER_PAGE;
    if (records.length < needed && records.length < totalRecords) {
      await fetchMore(records.length, needed - records.length);
    }
    setCurrentPage(targetPage);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  //計算頁籤陣列
  const calcPages = (currentPage, total, windowSize = 3) => {
    if (!Number.isFinite(total) || total <= 0) return [];

    const safeTotal = Math.floor(total);
    const half = Math.floor(windowSize / 2);

    const p = Math.min(Math.max(1, Math.floor(currentPage)), safeTotal);

    let start = p - half;
    let end = p + half;

    if (start < 1) {
      end += 1 - start;
      start = 1;
    }

    if (end > safeTotal) {
      start -= end - safeTotal;
      end = safeTotal;
      if (start < 1) start = 1;
    }

    const pages = [];
    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  };

  const total = Math.ceil(totalRecords / ITEMS_PER_PAGE); //總頁數
  const pagedData = records.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE,
  );
  let pageArray = calcPages(currentPage, total);

  return (
    <div className="point-record-page">
      {/* API 載入中（至少顯示 1 秒）*/}
      {isLoading && <Loading />}

      {/* 電腦板佈局 */}
      {!isMobile && (
        <div
          className="web-main"
          onMouseEnter={() => {
            setShowServiceMenu(false);
            setShowPersonalMenu(false);
          }}
        >
          <div className="title">
            <div className="title-left">
              <img src="../images/ic-apoint.png" alt="" />
              <span>點數紀錄</span>
            </div>
            <span className="title-note">僅顯示近三個月的紀錄</span>
          </div>

          {total > 0 && (
            <div className="records">
              {pagedData.map((record, idx) => (
                <div className="item-record" key={idx}>
                  <span className="change">
                    {`${formatChange(record.change)} A point`}
                  </span>

                  <div className="record-line"></div>

                  <div className="record-info">
                    <div className="item-info">
                      <span className="item-title">建立時間</span>
                      <span>{record.createdDate}</span>
                    </div>
                    <div className="item-info">
                      <span className="item-title">點數說明</span>
                      <span>{record.typeLabel || record.description}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {total > 0 && (
            <div className="record-selector">
              <img
                src={
                  currentPage - 1 == 0
                    ? "../images/ic-non-select-left.png"
                    : "../images/ic-select-left.png"
                }
                onClick={() => {
                  if (currentPage - 1 == 0) return;
                  goToPage(currentPage - 1);
                }}
              ></img>
              <div
                className={`pages ${total == 1 ? "one-page" : total == 2 ? "two-page" : ""}`}
              >
                {pageArray.map((number) => (
                  <span
                    key={number}
                    className={number == currentPage ? "current-page" : ""}
                    onClick={() => {
                      if (number == currentPage) return;
                      goToPage(number);
                    }}
                  >
                    {number}
                  </span>
                ))}
              </div>
              <img
                src={
                  currentPage == total
                    ? "../images/ic-non-select-right.png"
                    : "../images/ic-select-right.png"
                }
                onClick={() => {
                  if (currentPage == total) return;
                  goToPage(currentPage + 1);
                }}
              ></img>
            </div>
          )}

          {total == 0 && !isLoading && (
            <div className="no-record">暫無紀錄</div>
          )}

          <div className="divide"></div>

          {/*隱私政策與聯絡官方*/}
          <SiteFooter />
        </div>
      )}

      {/* 手機板佈局 */}
      {isMobile && (
        <div className="mobile-main">
          <div className="m-content">
            <div className="m-title">
              <div className="m-title-left">
                <img src="../images/ic-apoint.png" alt="" />
                <span>點數紀錄</span>
              </div>
              <span className="m-title-note">僅顯示近三個月的紀錄</span>
            </div>

            {total > 0 && (
              <div className="m-records">
                {pagedData.map((record, idx) => (
                  <div className="m-item-record" key={idx}>
                    <span className="m-change">
                      {`${formatChange(record.change)} A point`}
                    </span>

                    <div className="m-record-line"></div>

                    <div className="m-record-info">
                      <div className="m-item-info">
                        <span className="m-item-title">建立時間</span>
                        <span>{record.createdDate}</span>
                      </div>
                      <div className="m-item-info">
                        <span className="m-item-title">點數說明</span>
                        <span>{record.typeLabel || record.description}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {total > 0 && (
              <div className="m-record-selector">
                <img
                  src={
                    currentPage - 1 === 0
                      ? "../images/ic-non-select-left.png"
                      : "../images/ic-select-left.png"
                  }
                  onClick={() => {
                    if (currentPage - 1 !== 0) goToPage(currentPage - 1);
                  }}
                />
                <div
                  className={`m-pages ${total === 1 ? "one-page" : total === 2 ? "two-page" : ""}`}
                >
                  {pageArray.map((number) => (
                    <span
                      key={number}
                      className={number === currentPage ? "current-page" : ""}
                      onClick={() => {
                        if (number !== currentPage) goToPage(number);
                      }}
                    >
                      {number}
                    </span>
                  ))}
                </div>
                <img
                  src={
                    currentPage === total
                      ? "../images/ic-non-select-right.png"
                      : "../images/ic-select-right.png"
                  }
                  onClick={() => {
                    if (currentPage !== total) goToPage(currentPage + 1);
                  }}
                />
              </div>
            )}

            {total === 0 && !isLoading && (
              <div className="m-no-record">暫無紀錄</div>
            )}
          </div>

          <SiteFooter />
        </div>
      )}

      {/* 未登入訪客；關掉 → 留在當前頁 */}
      {showLoginDialog && (
        <GoogleLoginDialog
          open={showLoginDialog}
          onClose={() => {
            setShowLoginDialog(false);
          }}
          onSuccess={handleLoginSuccess}
        />
      )}

      {/* 已登入但未完成註冊；關掉 → 留在當前頁 */}
      <ConfirmDialog
        open={showRegisterPrompt}
        onClose={() => {
          setShowRegisterPrompt(false);
        }}
        onSuccess={() => {
          setShowRegisterPrompt(false);
          navigate("/register", { state: { from: registerFrom } });
        }}
        title="尚未完成註冊"
        message="點數管理需要完成註冊才能查看，<br>是否前往註冊頁完成？"
        confirmTxt="前往註冊"
      />
    </div>
  );
};

export default PointRecordCompontent;
